/**
 * Main Application Component
 *
 * Defines the application routing structure and authentication guards.
 * Uses React.lazy for code splitting to improve initial load performance.
 *
 * Routes are organized into:
 * - Public routes (login)
 * - Protected routes (requires authentication)
 * - Admin routes (requires ADMIN role)
 * - Module routes (sheilot, etc.)
 */

import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import RouteGuard from './components/RouteGuard';

// =============================================================================
// Loading Fallback
// =============================================================================

const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-primary-100 rounded-full" />
        <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-primary-600 rounded-full animate-spin" />
      </div>
      <p className="mt-4 text-gray-600 font-medium">טוען...</p>
    </div>
  </div>
);

// =============================================================================
// Lazy-loaded Page Imports (Code Splitting)
// =============================================================================

// Core pages
const Login = lazy(() => import('./pages/Login'));
const Home = lazy(() => import('./pages/Home'));
const ZeroAccess = lazy(() => import('./pages/ZeroAccess'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const Settings = lazy(() => import('./pages/Settings'));
const ThemeDemo = lazy(() => import('./pages/ThemeDemo'));

// Sheilot module pages (Custom Queries)
const SheilotQueryList = lazy(() => import('./modules/sheilot/pages/QueryList'));
const SheilotQueryRun = lazy(() => import('./modules/sheilot/pages/QueryRun'));
const SheilotQueryAdmin = lazy(() => import('./modules/sheilot/pages/QueryAdmin'));

// =============================================================================
// Application Routes
// =============================================================================

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ===================================================================== */}
        {/* PUBLIC ROUTES                                                        */}
        {/* ===================================================================== */}
        <Route
          path="/login"
          element={
            <RouteGuard type="public">
              <Login />
            </RouteGuard>
          }
        />

        {/* Theme Demo - public page for showcasing themes */}
        <Route path="/theme-demo" element={<ThemeDemo />} />

        {/* ===================================================================== */}
        {/* ZERO ACCESS ROUTE                                                    */}
        {/* ===================================================================== */}
        <Route
          path="/zero-access"
          element={
            <RouteGuard type="zero-access">
              <ZeroAccess />
            </RouteGuard>
          }
        />

        {/* ===================================================================== */}
        {/* PROTECTED ROUTES                                                     */}
        {/* ===================================================================== */}
        <Route
          path="/"
          element={
            <RouteGuard type="protected">
              <Home />
            </RouteGuard>
          }
        />

        {/* Settings page - accessible to all authenticated users */}
        <Route
          path="/settings"
          element={
            <RouteGuard type="protected">
              <Settings />
            </RouteGuard>
          }
        />

        {/* ===================================================================== */}
        {/* ADMIN ROUTES                                                         */}
        {/* ===================================================================== */}
        <Route
          path="/users"
          element={
            <RouteGuard type="admin">
              <UserManagement />
            </RouteGuard>
          }
        />

        {/* ===================================================================== */}
        {/* SHEILOT MODULE ROUTES                                                */}
        {/* ===================================================================== */}
        <Route
          path="/sheilot"
          element={
            <RouteGuard type="module" moduleId="sheilot">
              <SheilotQueryList />
            </RouteGuard>
          }
        />
        <Route
          path="/sheilot/query/:id"
          element={
            <RouteGuard type="module" moduleId="sheilot">
              <SheilotQueryRun />
            </RouteGuard>
          }
        />
        <Route
          path="/sheilot/admin"
          element={
            <RouteGuard type="admin">
              <SheilotQueryAdmin />
            </RouteGuard>
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

// =============================================================================
// Main App Component
// =============================================================================

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
